import { useCallback, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { fileToCompressedDataURL } from "../lib/imageUtils";
import { uploadImageFile } from "../lib/uploadImage";
import { toast } from "sonner";

export default function ImageDropUpload({
    value,
    onChange,
    maxEdge = 900,
    previewClassName = "aspect-video",
    testIdPrefix = "image-upload",
    helperText = "JPG / PNG · 自动压缩至 900px",
    preserveTransparency = false,
    accept = "image/*",
    uploadToServer = false,
    quality = 0.82,
}) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [processing, setProcessing] = useState(false);

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        setProcessing(true);
        try {
            if (uploadToServer) {
                onChange(await uploadImageFile(file, maxEdge, quality));
            } else {
                onChange(await fileToCompressedDataURL(file, maxEdge, quality, preserveTransparency));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "上传失败");
        } finally {
            setProcessing(false);
        }
    }, [maxEdge, onChange, preserveTransparency, uploadToServer, quality]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) handleFile(f);
    };

    if (value) {
        return (
            <div className="relative w-full">
                <div
                    className={`relative ${previewClassName} bg-gold-50 border border-slate-200 rounded-md overflow-hidden`}
                >
                    <img src={value} alt="预览" className="w-full h-full object-contain" />
                    <button
                        type="button"
                        data-testid={`${testIdPrefix}-remove-button`}
                        onClick={() => onChange("")}
                        className="absolute top-2 right-2 h-8 w-8 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-slate-900 transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <button
                    type="button"
                    data-testid={`${testIdPrefix}-replace-button`}
                    onClick={() => inputRef.current?.click()}
                    className="mt-2 text-xs text-gold-600 hover:text-gold-700 font-medium"
                >
                    更换图片
                </button>
                <input
                    ref={inputRef}
                    data-testid={`${testIdPrefix}-file-input`}
                    type="file"
                    accept={accept}
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
            </div>
        );
    }

    return (
        <div
            role="button"
            tabIndex={0}
            data-testid={`${testIdPrefix}-dropzone`}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative flex w-full flex-col items-center justify-center ${previewClassName} border-2 border-dashed rounded-md cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-gold-400 ${
                dragOver ? "border-gold-500 bg-gold-50" : "border-slate-300 bg-gold-50 hover:border-gold-400 hover:bg-gold-50/40"
            }`}
        >
            <input
                ref={inputRef}
                data-testid={`${testIdPrefix}-file-input`}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) { handleFile(e.target.files[0]); e.target.value = ""; } }}
            />
            {processing ? (
                <div className="text-sm text-gold-500">处理中...</div>
            ) : (
                <>
                    <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-2">
                        <Upload className="h-4 w-4 text-gold-500" />
                    </div>
                    <div className="text-sm font-medium text-slate-700">拖放图片到这里，或<span className="text-gold-600">点击选择</span></div>
                    <div className="text-xs text-gold-500 mt-1 flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" /> {helperText}
                    </div>
                </>
            )}
        </div>
    );
}
